import type React from 'react'
import { Form, Input, DatePicker, Select } from 'antd'
import { Horizontal, Vertical, Texto } from '@gravitate-js/excalibrr'
import type { InvoiceFormValues } from '@api/useSupplierReconciliation/types/invoice'
import { useInvoiceValidation } from '../hooks/useInvoiceValidation'
import { BILLING_METHOD_OPTIONS } from '../utils/constants'

interface InvoiceHeaderFormProps {
  form: Form<InvoiceFormValues>
  dropdownOptions: {
    suppliers: Array<{ value: string; label: string }>
    terminals: Array<{ value: string; label: string }>
    products: Array<{ value: string; label: string }>
    states: Array<{ value: string; label: string }>
  }
  metadataLoading: boolean
  onBillingMethodChange: (billingMethod: string) => void
}

export const InvoiceHeaderForm: React.FC<InvoiceHeaderFormProps> = ({
  form,
  dropdownOptions,
  metadataLoading,
  onBillingMethodChange,
}) => {
  const {
    getInvoiceNumberRules,
    getSupplierRules,
    getInvoiceDateRules,
    getInvoiceDueRules,
    getTerminalRules,
    getBillingMethodRules,
  } = useInvoiceValidation()

  return (
    <Vertical gap={16} border='all' bg='elevated' className='p-3' flex='0 0 320px' height='100%'>
      <Form.Item
        style={{ margin: 0 }}
        name='invoiceNumber'
        label={<Texto weight='bold'>Invoice Number</Texto>}
        rules={getInvoiceNumberRules()}
      >
        <Input placeholder='Enter invoice number' autoComplete='off' />
      </Form.Item>

      <Form.Item
        style={{ margin: 0 }}
        name='supplier'
        label={<Texto weight='bold'>Supplier</Texto>}
        rules={getSupplierRules()}
      >
        <Select
          placeholder='Select supplier'
          allowClear
          showSearch
          options={dropdownOptions.suppliers}
          loading={metadataLoading}
          filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>

      <Form.Item
        style={{ margin: 0 }}
        name='invoiceDate'
        label={<Texto weight='bold'>Invoice Date</Texto>}
        rules={getInvoiceDateRules(form)}
      >
        <DatePicker
          showTime={{ use12Hours: true, format: 'hh:mm A' }}
          format='YYYY-MM-DD hh:mm A'
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item
        style={{ margin: 0 }}
        name='invoiceDue'
        label={<Texto weight='bold'>Invoice Due</Texto>}
        rules={getInvoiceDueRules(form)}
      >
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        style={{ margin: 0 }}
        name='terminal'
        label={<Texto weight='bold'>Terminal</Texto>}
        rules={getTerminalRules()}
      >
        <Select
          placeholder='Select terminal'
          allowClear
          showSearch
          options={dropdownOptions.terminals}
          loading={metadataLoading}
          filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>

      <Form.Item
        style={{ margin: 0 }}
        name='billingBy'
        label={<Texto weight='bold'>Billing Method</Texto>}
        rules={getBillingMethodRules()}
      >
        <Select
          placeholder='Select billing method'
          allowClear
          options={BILLING_METHOD_OPTIONS}
          onChange={onBillingMethodChange}
        />
      </Form.Item>
    </Vertical>
  )
}
